import { BookOpen } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CardContent, CardHeader, CardTitle, CardV2 } from "../../components/CardV2/CardV2";
import { PermissionFlags } from "../../components/PermissionFlags/PermissionFlags";
import PlainTextButton from "../../components/PlainTextButton/PlainTextButton";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { useGetCollections } from "../../hooks/useGetCollections";

const SEE_ALL_AFTER = 5;

function HomeCataloguesCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoadingCollections, collections } = useGetCollections();

  const handleCreate = () => {
    if (collections.length === 0) {
      navigate("/onboarding/catalog?from=home");
      return;
    }
    navigate("/catalogues?openForm=true");
  };

  return (
    <PermissionFlags requiredFlags={["see-marketing-collections"]}>
      <CardV2 className="flex h-[16rem] max-h-[16rem] min-h-0 flex-col overflow-hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            {t("home.section.catalogues.title", "Catalogues")}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {isLoadingCollections ? (
            <Skeleton className="h-8 w-full" />
          ) : collections.length === 0 ? (
            <p className="flex flex-1 items-center text-sm text-muted-foreground">
              {t(
                "home.section.catalogues.empty",
                "Create your first catalogue here. We'll walk you through a name, a few sample items, and who can see it."
              )}
            </p>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
              <ul className="space-y-2">
                {collections.map((collection) => (
                  <li key={collection.id ?? collection.name}>
                    <button
                      type="button"
                      className="w-full truncate rounded-lg bg-muted px-4 py-3 text-left text-sm font-medium hover:bg-muted/80"
                      onClick={() => navigate("/catalogues")}
                    >
                      {collection.name}
                    </button>
                  </li>
                ))}
              </ul>
              {collections.length > SEE_ALL_AFTER && (
                <PlainTextButton
                  text={t("home.section.catalogues.see_all", "See all") as string}
                  onClick={() => navigate("/catalogues")}
                  actionName="home_catalogues_see_all"
                />
              )}
            </div>
          )}
          <div className="mt-auto pt-4">
            <PermissionFlags requiredFlags={["create-marketing-collections"]}>
              <PrimaryButton
                text={t("home.section.catalogues.cta", "Create catalog")!}
                fn={handleCreate}
                isDisabled={isLoadingCollections}
                actionName="home_catalogues_create"
                className="w-full sm:w-full"
              />
            </PermissionFlags>
          </div>
        </CardContent>
      </CardV2>
    </PermissionFlags>
  );
}

export default HomeCataloguesCard;
